/** Agent SSOT field keys — keep in sync with PassengersData.PASSENGER_KEYS / ROOM_KEYS */
const PK = {
  TITLE: "title",
  GENDER: "gender",
  FIRSTNAME: "firstname",
  LASTNAME: "lastname",
  CELL: "cell",
  EMAIL: "email",
  AGE: "age",
  KIS_SEARCH_AGE: "kisSearchAge",
};

const RK = {
  ROOM_SET: "roomSet",
  ROOM_TYPE: "roomType",
  SEQ_NUM: "@seqNum",
};

export const INITIAL_PASSENGER = {
  [PK.TITLE]: "",
  [PK.GENDER]: "",
  [PK.FIRSTNAME]: "",
  [PK.LASTNAME]: "",
  [PK.CELL]: "",
  [PK.EMAIL]: "",
  [PK.AGE]: "01/01/1990",
};

export const INITIAL_KID_PASSENGER = {
  [PK.TITLE]: "",
  [PK.GENDER]: "",
  [PK.FIRSTNAME]: "",
  [PK.LASTNAME]: "",
  [PK.AGE]: "01/01/2020",
};

function defaultInfantLdsAge() {
  // Keep placeholder infants under age 2 for mid-year 2026+ validation.
  const year = new Date().getFullYear();
  return `15/06/${year}`;
}

export const INITIAL_INFANT_PASSENGER = {
  [PK.TITLE]: "",
  [PK.FIRSTNAME]: "",
  [PK.LASTNAME]: "",
  [PK.AGE]: defaultInfantLdsAge(),
};

export const INITIAL_ROOM_SLOT = {
  [RK.ROOM_SET]: "",
  [RK.ROOM_TYPE]: "",
  [RK.SEQ_NUM]: "",
};

/**
 * Agent SSOT: build roomsAndPaxInfo skeleton from room composition (adults / kids / infants per room).
 * @param {{ rooms?: Array<{ adults?: number, kids?: number, infants?: number, kidsAges?: number[] }> }|null|undefined} roomInfo
 * @returns {Array}
 */
export function buildInitialRoomsAndPaxInfo(roomInfo) {
  const roomsComposition = Array.isArray(roomInfo?.rooms) ? roomInfo.rooms : [];
  if (roomsComposition.length === 0) return [];

  let paxCounter = 0;

  function getInitialPassengersByLength(length, params) {
    if (!length) return [];
    return Array.from({ length }, (_, i) => {
      paxCounter += 1;
      if (params.adults) {
        return { ...INITIAL_PASSENGER, "@seqNum": paxCounter };
      }
      if (params.kids) {
        return {
          ...INITIAL_KID_PASSENGER,
          [PK.KIS_SEARCH_AGE]: params.kidsAges?.[i],
          "@seqNum": paxCounter,
        };
      }
      if (params.infants) {
        return {
          ...INITIAL_INFANT_PASSENGER,
          [PK.AGE]: defaultInfantLdsAge(),
          "@seqNum": paxCounter,
        };
      }
      return { "@seqNum": paxCounter };
    });
  }

  let roomCounter = 0;
  return roomsComposition.map((room) => {
    roomCounter += 1;
    const kidsAges = Array.isArray(room.kidsAges) ? room.kidsAges : [];
    return {
      adults: getInitialPassengersByLength(Math.max(0, Number(room.adults) || 0), {
        adults: true,
      }),
      kids: getInitialPassengersByLength(Math.max(0, Number(room.kids) || 0), {
        kids: true,
        kidsAges,
      }),
      infants: getInitialPassengersByLength(Math.max(0, Number(room.infants) || 0), {
        infants: true,
      }),
      room: { ...INITIAL_ROOM_SLOT },
      "@seqNum": roomCounter,
    };
  });
}

/**
 * @param {unknown} roomsAndPaxInfo
 */
export function countRenderedPassengers(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo)) return 0;
  return roomsAndPaxInfo.reduce(
    (sum, room) =>
      sum +
      (room?.adults?.length ?? 0) +
      (room?.kids?.length ?? 0) +
      (room?.infants?.length ?? 0),
    0,
  );
}

/**
 * @param {{ rooms?: Array }|null|undefined} roomInfo
 */
export function countExpectedPassengersFromRoomInfo(roomInfo) {
  const rooms = Array.isArray(roomInfo?.rooms) ? roomInfo.rooms : [];
  return rooms.reduce(
    (sum, room) =>
      sum +
      (Number(room?.adults) || 0) +
      (Number(room?.kids) || 0) +
      (Number(room?.infants) || 0),
    0,
  );
}

/**
 * @param {{ rooms?: Array }|null|undefined} roomInfo
 */
export function summarizeRoomCompositionCounts(roomInfo) {
  const rooms = Array.isArray(roomInfo?.rooms) ? roomInfo.rooms : [];
  return rooms.reduce(
    (acc, room) => ({
      adults: acc.adults + (Number(room?.adults) || 0),
      children: acc.children + (Number(room?.kids) || 0),
      infants: acc.infants + (Number(room?.infants) || 0),
    }),
    { adults: 0, children: 0, infants: 0 },
  );
}

/**
 * Stable signature for search room composition — used to re-sync checkout pax slots.
 * @param {{ rooms?: Array }|null|undefined} roomInfo
 */
export function buildRoomCompositionSignature(roomInfo) {
  const rooms = Array.isArray(roomInfo?.rooms) ? roomInfo.rooms : [];
  return JSON.stringify(
    rooms.map((room) => ({
      adults: Number(room?.adults) || 0,
      kids: Number(room?.kids) || 0,
      infants: Number(room?.infants) || 0,
      kidsAges: Array.isArray(room?.kidsAges) ? room.kidsAges : [],
    })),
  );
}

/**
 * @param {unknown} roomsAndPaxInfo
 * @param {{ rooms?: Array }|null|undefined} roomInfo
 */
export function paxSlotsMatchRoomComposition(roomsAndPaxInfo, roomInfo) {
  const composition = Array.isArray(roomInfo?.rooms) ? roomInfo.rooms : [];
  const slots = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  if (composition.length === 0 || slots.length === 0) return false;
  if (composition.length !== slots.length) return false;
  return composition.every((room, index) => {
    const slot = slots[index];
    return (
      (slot?.adults?.length ?? 0) === (Number(room?.adults) || 0) &&
      (slot?.kids?.length ?? 0) === (Number(room?.kids) || 0) &&
      (slot?.infants?.length ?? 0) === (Number(room?.infants) || 0)
    );
  });
}

/**
 * @param {unknown} roomsAndPaxInfo
 */
export function slotsHavePassengerFormData(roomsAndPaxInfo) {
  const slots = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  return slots.some((slot) => {
    const people = [
      ...(Array.isArray(slot?.adults) ? slot.adults : []),
      ...(Array.isArray(slot?.kids) ? slot.kids : []),
      ...(Array.isArray(slot?.infants) ? slot.infants : []),
    ];
    return people.some((person) => {
      if (!person || typeof person !== "object") return false;
      return (
        String(person.firstname ?? "").trim() ||
        String(person.lastname ?? "").trim() ||
        String(person.email ?? "").trim() ||
        String(person.cell ?? "").trim() ||
        String(person.phone ?? "").trim()
      );
    });
  });
}
